import type { ApiClient } from './client'
import type { components } from '@aurigaengineering/contracts-admin'

export type QuestionSummary = components['schemas']['QuestionSummary']
export type QuestionPage = components['schemas']['QuestionPageView']
export type Question = components['schemas']['QuestionView']
export type QuestionEdit = components['schemas']['QuestionEdit']
export type UploadOutcome = components['schemas']['UploadOutcomeView']
export type PreviewLaunch = components['schemas']['PreviewLaunch']
export type TryOutcome = components['schemas']['TryOutcome']
export type TopicCount = components['schemas']['TopicCountView']
export type QuestionFeedback = components['schemas']['QuestionFeedbackView']

/**
 * The filter boxes, one per column. Every one that carries a word narrows the page; the
 * empty ones are left off the query string entirely rather than sent as blanks.
 */
export interface QuestionFilters {
  exam?: string
  placement?: string
  kind?: string
  id?: number
  topicCode?: string
  topic?: string
  componentCode?: string
  stem?: string
  skip?: number
  take?: number
}

/**
 * What the Questions Manager does.
 *
 * The screen talks to this rather than to the typed client, for one reason: a test can hand
 * it a stand-in. Everything below is the contract's own shape -- these are the generated
 * types, not a second set written by hand -- so what a test exercises is the screen's
 * behaviour and not a fiction about the wire.
 */
export interface QuestionsApi {
  list(filters: QuestionFilters): Promise<QuestionPage>
  read(id: number): Promise<Question>
  /** Many files at once: loose .yaml and .json, or .zip archives of them. */
  upload(files: File[]): Promise<UploadOutcome>
  save(id: number, question: QuestionEdit): Promise<Question>
  remove(id: number): Promise<void>
  /** Mints a Simulator link for the selected Questions. The API names the address, not us. */
  preview(ids: number[]): Promise<PreviewLaunch>
  /**
   * One file, read and validated but never stored, so a Question can be looked at before
   * anybody decides it is worth keeping. Refusals come back in the outcome rather than as
   * an error: a file the validator turned away is an answer, not a failed request.
   */
  tryFile(yaml: string): Promise<TryOutcome>
  /**
   * The Question Schema, annotated, exactly as the validator reads it. Fetched rather
   * than held here: one artefact governs uploads and documents them, so the screen cannot
   * drift from the rules (app:R35).
   */
  schema(): Promise<string>
  /**
   * The whole Bank counted by Exam and Topic Code, both kinds on each row. No filters:
   * it is dozens of rows, so a screen showing one Exam picks it from what it already has.
   */
  topics(): Promise<TopicCount[]>
  /** What Students said about one Question through Q Feedback, newest first (app:R63). */
  feedback(id: number): Promise<QuestionFeedback[]>
  /** Every Q Feedback on one Question, read: the Admin has opened it. */
  markRead(question: number): Promise<void>
  /** Q Feedback on one Exam's Questions that nobody has read, for the nav. */
  unreadFeedback(exam: string): Promise<number>
}

export function questionsApi(client: ApiClient): QuestionsApi {
  return {
    async list(filters) {
      const { data, error } = await client.GET('/admin/questions', {
        params: { query: given(filters) },
      })
      return data ?? failed(error)
    },

    async read(id) {
      const { data, error } = await client.GET('/admin/questions/{id}', {
        params: { path: { id } },
      })
      return data ?? failed(error)
    },

    async upload(files) {
      const form = new FormData()
      for (const file of files) {
        form.append('files', file, file.name)
      }

      // FormData goes through the client untouched: openapi-fetch passes it to fetch as
      // it stands and sets no content-type, which is what makes the browser write the
      // multipart boundary. The cast is only because the contract types this body as the
      // text/yaml string a single-file upload sends.
      const { data, error } = await client.POST('/admin/questions/upload', {
        body: form as unknown as string,
      })
      return data ?? failed(error)
    },

    async save(id, question) {
      const { data, error } = await client.PUT('/admin/questions/{id}', {
        params: { path: { id } },
        body: question,
      })
      return data ?? failed(error)
    },

    async remove(id) {
      const { error } = await client.DELETE('/admin/questions/{id}', {
        params: { path: { id } },
      })
      if (error) failed(error)
    },

    async schema() {
      const { data, error } = await client.GET('/admin/questions/schema', {})
      return data?.yaml ?? failed(error)
    },

    async topics() {
      const { data, error } = await client.GET('/admin/questions/topics', {})
      return data ?? failed(error)
    },

    async preview(ids) {
      const { data, error } = await client.POST('/admin/questions/preview', {
        body: { ids },
      })
      return data ?? failed(error)
    },

    async feedback(id) {
      const { data, error } = await client.GET('/admin/questions/{id}/feedback', {
        params: { path: { id } },
      })
      return data ?? failed(error)
    },

    async markRead(question) {
      const { error } = await client.POST('/admin/questions/{id}/feedback/read', {
        params: { path: { id: question } },
      })
      if (error) failed(error)
    },

    async unreadFeedback(exam) {
      const { data, error } = await client.GET('/admin/questions/feedback/unread', {
        params: { query: { exam } },
      })
      return data?.count ?? failed(error)
    },

    async tryFile(yaml) {
      const { data, error } = await client.POST('/admin/questions/try', {
        body: { yaml },
      })
      return data ?? failed(error)
    },
  }
}

/** The most a page holds: the API caps it here, however many are asked for. */
const PAGE = 200

/**
 * Every row the filters match, page by page. A single call for more than a page gets a page,
 * silently, so a screen that wants the whole Bank has to walk it.
 */
export async function everyRow(api: QuestionsApi, filters: QuestionFilters): Promise<QuestionSummary[]> {
  const rows: QuestionSummary[] = []
  for (;;) {
    const page = await api.list({ ...filters, skip: rows.length, take: PAGE })
    rows.push(...page.rows)
    if (page.rows.length === 0 || rows.length >= page.total) return rows
  }
}

/**
 * Only the filters that were filled in. A blank box is not a filter, and sending it as an
 * empty string would make the API narrow on nothing at all.
 */
function given(filters: QuestionFilters): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && String(value).trim() !== '',
    ),
  )
}

/**
 * Turns whatever the API refused with into an Error the screen can show.
 *
 * ProblemDetails where the API sent one -- its `detail` is written to be read by the person
 * who caused it -- and the status alone where it did not, which is what a 401 or a 403 from
 * the pipeline looks like.
 */
function failed(error: unknown): never {
  const problem = error as { detail?: string; title?: string } | undefined

  throw new Error(problem?.detail ?? problem?.title ?? 'The API refused that.')
}
